import {createContext, FC, ReactNode, useCallback, useContext, useMemo, useState} from 'react';

const STORAGE_KEY = 'selectedSections';

type SelectedSectionContextValue = {
	selectedSections: string[];
	addSelectedSection: (section: string) => void;
	clearSelectedSections: () => void;
};

const SelectedSectionContext = createContext<SelectedSectionContextValue | undefined>(undefined);

const loadPersisted = (): string[] => {
	try {
		const raw = sessionStorage.getItem(STORAGE_KEY);
		if (!raw) return [];
		const parsed = JSON.parse(raw);
		return Array.isArray(parsed) ? parsed.filter((item) => typeof item === 'string') : [];
	} catch {
		return [];
	}
};

const persist = (sections: string[]) => {
	sessionStorage.setItem(STORAGE_KEY, JSON.stringify(sections));
};

export const SelectedSectionProvider: FC<{children: ReactNode}> = ({children}) => {
	const [selectedSections, setSelectedSections] = useState<string[]>(() => loadPersisted());

	const addSelectedSection = useCallback((section: string) => {
		setSelectedSections((prev) => {
			if (prev.includes(section)) return prev;
			const next = [...prev, section];
			persist(next);
			return next;
		});
	}, []);

	const clearSelectedSections = useCallback(() => {
		setSelectedSections([]);
		sessionStorage.removeItem(STORAGE_KEY);
	}, []);

	const value = useMemo(
		() => ({selectedSections, addSelectedSection, clearSelectedSections}),
		[selectedSections, addSelectedSection, clearSelectedSections]
	);

	return <SelectedSectionContext.Provider value={value}>{children}</SelectedSectionContext.Provider>;
};

export const useSelectedSection = (): SelectedSectionContextValue => {
	const ctx = useContext(SelectedSectionContext);
	if (!ctx) {
		throw new Error('useSelectedSection must be used within SelectedSectionProvider');
	}
	return ctx;
};

